import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart } from "lucide-react";
import { inr } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function ProductCard({ product, index = 0 }) {
  const { toggleWishlist, wishlist } = useCart();
  const wished = wishlist.some((p) => p.product_id === product.product_id);
  const saving = product.mrp > product.price ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="group"
      data-testid={`product-card-${product.product_id}`}
    >
      <div className="relative overflow-hidden bg-sand aspect-[3/4]">
        <Link to={`/product/${product.product_id}`} data-testid={`product-link-${product.product_id}`}>
          <img src={product.images?.[0]} alt={product.name} loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
        </Link>
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {product.is_preloved && <span className="text-[9px] tracking-[0.2em] uppercase px-2 py-1 bg-ink/85 text-ivory">Pre-Owned{product.grade ? ` · Grade ${product.grade}` : ""}</span>}
          {(product.labels || []).filter((l) => !["PRE-LOVED", "PRE-OWNED"].includes(l)).map((l) => (
            <span key={l} className={`text-[9px] tracking-[0.2em] uppercase px-2 py-1 ${l === "VERIFIED" ? "bg-gold text-ink" : "bg-ink/85 text-ivory"}`}>{l}</span>
          ))}
          {!product.is_preloved && product.is_new_arrival && <span className="text-[9px] tracking-[0.2em] uppercase px-2 py-1 bg-ink/85 text-ivory">New</span>}
          {saving > 0 && !product.ask_for_price && <span className="text-[9px] tracking-[0.2em] uppercase px-2 py-1 bg-wine text-ivory">Save {saving}%</span>}
        </div>
        <button onClick={() => toggleWishlist(product)} data-testid={`wishlist-toggle-${product.product_id}`}
          className="absolute top-3 right-3 w-8 h-8 bg-ivory/90 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          aria-label="Wishlist">
          <Heart size={14} strokeWidth={1.5} className={wished ? "fill-wine text-wine" : ""} />
        </button>
      </div>
      <div className="pt-4">
        <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">{product.brand}</p>
        <Link to={`/product/${product.product_id}`} className="block mt-1 font-serif text-lg leading-tight hover:text-wine transition-colors">{product.name}</Link>
        {product.ask_for_price ? (
          <Link to={`/product/${product.product_id}`} data-testid={`ask-price-${product.product_id}`}
            className="mt-1.5 inline-block text-[11px] tracking-[0.2em] uppercase text-wine border-b border-wine/40 pb-0.5">Ask for Price</Link>
        ) : (
          <div className="mt-1.5 flex items-baseline gap-2 font-mono text-sm">
            <span data-testid={`price-${product.product_id}`}>{inr(product.price)}</span>
            {product.mrp > product.price && <span className="text-muted-foreground line-through text-xs">{inr(product.mrp)}</span>}
          </div>
        )}
        <p className="mt-1 text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
          {product.stock <= 3 ? `${product.stock} Available` : "In Stock"}
          {product.is_preloved && <span className="text-gold"> · {product.condition}</span>}
        </p>
      </div>
    </motion.div>
  );
}
